/**
 * DigitalPage — Area "Digital" do CROSS M3.
 *
 * Estrutura das 4 abas:
 *   /digital            → Visao geral (Instagram + Meta Ads consolidados)
 *   /digital/instagram  → Instagram Analyzer (Nivel B) — ver AnalyzerInstagramTab.tsx
 *   /digital/meta-ads   → Meta Ads (source=meta_ads)
 *   /digital/campanhas  → Analise de campanhas (reusa AdsCampaigns*)
 *
 * Decisoes de implementacao:
 *   - Abas implementadas como tablist/tab/tabpanel com URL refletida via
 *     NavLink + Outlet. A aba ativa e derivada do pathname atual.
 *   - Dados buscados via /kpi/snapshot?category=funnel&source=<X> — exato
 *     mesmo endpoint que DashboardFourPsPage, sem campo novo.
 *   - KPIs de Instagram na Visao Geral: alcance, seguidores, engajamento, curtidas, posts.
 *   - Aba Instagram: AnalyzerInstagramTab — consome /analyzer/profile e /analyzer/report.
 *   - KPIs de Meta Ads: investimento, CTR, CPC, CPM, impressoes, cliques.
 *   - Aba Campanhas monta AdsCampaignsListPage/Detail/Compare dentro do tabpanel.
 *   - Visao geral: ambas as fontes em paralelo, consolidadas.
 *   - Estados honestos em todas as abas: loading, vazio, erro, sem dado no periodo.
 *   - Zero string hardcoded fora de i18n.ts.
 *   - A11y AA: role=tablist/tab/tabpanel, aria-selected, aria-controls, keyboard nav.
 *   - F2 funcional — microinteracoes finas sao F3.
 *   - F3 — Faixa informativa de periodo nas abas Visao geral, Instagram, Meta Ads.
 *     O botao "Clique aqui" rola ao topo e foca o seletor de periodo da topbar
 *     (#topbar-period-select), sem duplicar o seletor.
 *
 * F3 — 2026-06-09
 */

import '../styles/screen-digital.css';
import { useEffect, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { SkeletonGrid } from '../components/Skeleton';
import { useFilterContext } from '../lib/filter-context';
import { DigitalPeriodBanner } from '../components/DigitalPeriodBanner';

// A aba Instagram usa o Analyzer (Nivel B) — implementado em AnalyzerInstagramTab.tsx.
export { DigitalInstagramTab } from './AnalyzerInstagramTab';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface KpiItem {
  readonly id: string;
  readonly title: string;
  readonly value: string | null;
  readonly unit?: string;
  readonly delta?: number | null;
  readonly description?: string;
  readonly source?: string;
}

interface FunnelData {
  readonly visibilidade: readonly KpiItem[];
  readonly engajamento: readonly KpiItem[];
  readonly trafego: readonly KpiItem[];
  readonly vendas: readonly KpiItem[];
  readonly headline?: string;
}

type LoadState<D> =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: D }
  | { kind: 'error' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildSnapshotUrl(
  source: 'instagram' | 'meta_ads',
  periodMode: string,
  appliedYear: number,
  appliedMonth: number,
): string {
  const base = `/kpi/snapshot?category=funnel&source=${source}`;
  if (periodMode === 'month') return `${base}&year=${appliedYear}&month=${appliedMonth}`;
  return `${base}&period=${periodMode}`;
}

function isColdStart(data: FunnelData): boolean {
  return (
    data.visibilidade.length === 0 &&
    data.engajamento.length === 0 &&
    data.trafego.length === 0 &&
    data.vendas.length === 0
  );
}

/** Retorna null quando o KPI nao tem dado real para o periodo. */
function resolveValue(kpi: KpiItem): string | null {
  if (kpi.value === null || kpi.value === undefined || kpi.value === 'N/A') return null;
  if (kpi.unit !== undefined && kpi.unit.length > 0) return `${kpi.value} ${kpi.unit}`;
  return kpi.value;
}

/** True quando o KPI nao tem dado real para o periodo. */
function kpiHasNoData(kpi: KpiItem): boolean {
  return kpi.value === null || kpi.value === undefined || kpi.value === 'N/A';
}

// Filtra KPIs digitais de Instagram: visibilidade + engajamento
function igKpisFrom(data: FunnelData): readonly KpiItem[] {
  return [...data.visibilidade, ...data.engajamento];
}

// Filtra KPIs de Meta Ads: trafego (investimento, CTR, CPC, CPM etc)
function adsKpisFrom(data: FunnelData): readonly KpiItem[] {
  return [...data.trafego];
}

// ---------------------------------------------------------------------------
// Componente: KPI card individual com ref chip
// ---------------------------------------------------------------------------

interface DigitalKpiCardProps {
  readonly kpi: KpiItem;
  readonly source: 'ig' | 'ads';
}

function DigitalKpiCard({ kpi, source }: DigitalKpiCardProps): JSX.Element {
  const value = resolveValue(kpi);
  const noData = value === null;

  const deltaClass =
    kpi.delta === undefined || kpi.delta === null
      ? ''
      : kpi.delta > 0
        ? 'digital-kpi-delta digital-kpi-delta--up'
        : kpi.delta < 0
          ? 'digital-kpi-delta digital-kpi-delta--down'
          : 'digital-kpi-delta digital-kpi-delta--flat';

  const deltaIcon =
    kpi.delta !== undefined && kpi.delta !== null
      ? kpi.delta > 0
        ? 'trending_up'
        : kpi.delta < 0
          ? 'trending_down'
          : 'trending_flat'
      : null;

  const sourceLabel = source === 'ig' ? 'Instagram' : 'Meta Ads';
  const dotClass = source === 'ig' ? 'digital-ref-chip-dot--ig' : 'digital-ref-chip-dot--ads';

  const noDataMsg = t('digital.noDataForPeriod');

  return (
    <article
      className="digital-kpi-card"
      aria-label={noData ? `${kpi.title}: ${noDataMsg}` : `${kpi.title}: ${value}`}
    >
      <p className="digital-kpi-label">{kpi.title}</p>
      {noData ? (
        <p className="digital-kpi-no-data" role="note">
          {noDataMsg}
        </p>
      ) : (
        <p className="digital-kpi-value">{value}</p>
      )}
      {!noData && kpi.delta !== undefined && kpi.delta !== null ? (
        <span
          className={deltaClass}
          aria-label={`${kpi.delta > 0 ? '+' : ''}${kpi.delta.toFixed(1)}%`}
        >
          {deltaIcon !== null ? (
            <span className="material-symbols-rounded" style={{ fontSize: 12 }} aria-hidden="true">
              {deltaIcon}
            </span>
          ) : null}
          {kpi.delta > 0 ? '+' : ''}
          {kpi.delta.toFixed(1)}%
        </span>
      ) : null}
      {!noData && kpi.description !== undefined && kpi.description.length > 0 ? (
        <p className="digital-kpi-description">{kpi.description}</p>
      ) : null}
      <span className="digital-ref-chip" aria-label={`via ${sourceLabel}`}>
        <span className={`digital-ref-chip-dot ${dotClass}`} aria-hidden="true" />
        {sourceLabel}
      </span>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Componente: Grade de KPI cards
// ---------------------------------------------------------------------------

interface DigitalKpiGridProps {
  readonly kpis: readonly KpiItem[];
  readonly source: 'ig' | 'ads';
  readonly groupLabel: string;
}

function DigitalKpiGroup({ kpis, source, groupLabel }: DigitalKpiGridProps): JSX.Element | null {
  if (kpis.length === 0) return null;

  // Se TODOS os KPIs da secao nao tem dado no periodo, mostrar mensagem uma unica vez.
  const allNoData = kpis.every((k) => kpiHasNoData(k));

  return (
    <div className="digital-kpi-group">
      <p className="digital-kpi-group-label">{groupLabel}</p>
      {allNoData ? (
        <p className="digital-section-no-data" role="note">
          {t('digital.noDataForPeriodSection')}
        </p>
      ) : (
        <div className="digital-kpi-grid" role="list">
          {kpis.map((kpi) => (
            <DigitalKpiCard key={kpi.id} kpi={kpi} source={source} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Estado vazio
// ---------------------------------------------------------------------------

interface DigitalEmptyProps {
  readonly icon: string;
  readonly title: string;
  readonly desc: string;
  readonly ctaLabel: string;
  readonly ctaTo: string;
}

function DigitalEmpty({ icon, title, desc, ctaLabel, ctaTo }: DigitalEmptyProps): JSX.Element {
  return (
    <div className="digital-empty-state" aria-live="polite">
      <span className="digital-empty-icon material-symbols-rounded" aria-hidden="true">
        {icon}
      </span>
      <p className="digital-empty-title">{title}</p>
      <p className="digital-empty-desc">{desc}</p>
      <Link to={ctaTo} className="digital-empty-cta">
        {ctaLabel}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Estado de erro
// ---------------------------------------------------------------------------

interface DigitalErrorProps {
  readonly onRetry: () => void;
}

function DigitalError({ onRetry }: DigitalErrorProps): JSX.Element {
  return (
    <div className="digital-error-state" role="alert">
      <p className="digital-error-message">{t('digital.error')}</p>
      <button type="button" className="digital-retry-btn" onClick={onRetry}>
        <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
          refresh
        </span>
        {t('digital.retryLabel')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook: carrega snapshot por fonte
// ---------------------------------------------------------------------------

function useDigitalSnapshot(
  source: 'instagram' | 'meta_ads',
  periodMode: string,
  appliedYear: number,
  appliedMonth: number,
) {
  const [state, setState] = useState<LoadState<FunnelData>>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const url = buildSnapshotUrl(source, periodMode, appliedYear, appliedMonth);

    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<FunnelData | null>(url);
        if (cancelled) return;
        if (data === null || isColdStart(data)) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [source, periodMode, appliedYear, appliedMonth, retryKey]);

  const retry = useCallback(() => setRetryKey((k) => k + 1), []);
  return { state, retry };
}

// ---------------------------------------------------------------------------
// Aba: Visao geral do digital (Instagram + Meta Ads)
// ---------------------------------------------------------------------------

export function DigitalOverviewTab(): JSX.Element {
  const { applied } = useFilterContext();
  const { periodMode, appliedYear, appliedMonth } = applied;

  const { state: igState, retry: retryIg } = useDigitalSnapshot(
    'instagram',
    periodMode,
    appliedYear,
    appliedMonth,
  );
  const { state: adsState, retry: retryAds } = useDigitalSnapshot(
    'meta_ads',
    periodMode,
    appliedYear,
    appliedMonth,
  );

  const isLoading = igState.kind === 'loading' || adsState.kind === 'loading';
  const hasError = igState.kind === 'error' || adsState.kind === 'error';
  const igReady = igState.kind === 'ready';
  const adsReady = adsState.kind === 'ready';
  const bothEmpty = igState.kind === 'empty' && adsState.kind === 'empty';

  const igKpis = igReady ? igKpisFrom(igState.data) : [];
  const adsKpis = adsReady ? adsKpisFrom(adsState.data) : [];

  if (isLoading) {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-visao-geral"
        aria-labelledby="digital-tab-visao-geral"
        className="digital-tabpanel"
      >
        <SkeletonGrid count={6} />
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      id="digital-tabpanel-visao-geral"
      aria-labelledby="digital-tab-visao-geral"
      className="digital-tabpanel"
    >
      <div className="digital-section-header">
        <div className="digital-section-header-text">
          <h2 className="digital-section-title">{t('digital.overviewTitle')}</h2>
          <p className="digital-section-sub">{t('digital.overviewSub')}</p>
        </div>
        <DigitalPeriodBanner />
      </div>

      {hasError ? (
        <DigitalError
          onRetry={() => {
            retryIg();
            retryAds();
          }}
        />
      ) : null}

      {bothEmpty && !hasError ? (
        <DigitalEmpty
          icon="hub"
          title={t('digital.coldStartTitle')}
          desc={t('digital.coldStart')}
          ctaLabel={t('digital.coldStartCta')}
          ctaTo="/sources"
        />
      ) : null}

      {igKpis.length > 0 ? (
        <DigitalKpiGroup kpis={igKpis} source="ig" groupLabel={t('digital.groupReach')} />
      ) : null}

      {adsKpis.length > 0 ? (
        <DigitalKpiGroup kpis={adsKpis} source="ads" groupLabel={t('digital.groupPaid')} />
      ) : null}

      {igState.kind === 'empty' && !hasError && igKpis.length === 0 && adsKpis.length > 0 ? (
        <div className="digital-kpi-group">
          <p className="digital-kpi-group-label">{t('digital.groupReach')}</p>
          <div className="digital-empty-state" aria-live="polite">
            <p className="digital-empty-desc">{t('digital.emptyNoSource')}</p>
          </div>
        </div>
      ) : null}

      {adsState.kind === 'empty' && !hasError && adsKpis.length === 0 && igKpis.length > 0 ? (
        <div className="digital-kpi-group">
          <p className="digital-kpi-group-label">{t('digital.groupPaid')}</p>
          <div className="digital-empty-state" aria-live="polite">
            <p className="digital-empty-desc">{t('digital.emptyNoSource')}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: Meta Ads
// ---------------------------------------------------------------------------

export function DigitalMetaAdsTab(): JSX.Element {
  const { applied } = useFilterContext();
  const { periodMode, appliedYear, appliedMonth } = applied;

  const { state, retry } = useDigitalSnapshot('meta_ads', periodMode, appliedYear, appliedMonth);

  if (state.kind === 'loading') {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-meta-ads"
        aria-labelledby="digital-tab-meta-ads"
        className="digital-tabpanel"
      >
        <SkeletonGrid count={4} />
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      id="digital-tabpanel-meta-ads"
      aria-labelledby="digital-tab-meta-ads"
      className="digital-tabpanel"
    >
      <div className="digital-section-header">
        <div className="digital-section-header-text">
          <h2 className="digital-section-title">{t('digital.metaAdsTitle')}</h2>
          <p className="digital-section-sub">{t('digital.metaAdsSub')}</p>
        </div>
        <DigitalPeriodBanner />
      </div>

      {state.kind === 'error' ? <DigitalError onRetry={retry} /> : null}

      {state.kind === 'empty' ? (
        <DigitalEmpty
          icon="campaign"
          title={t('digital.emptyNoSource')}
          desc={t('digital.emptyNoSourceDesc')}
          ctaLabel={t('digital.emptyNoSourceCta')}
          ctaTo="/sources"
        />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {adsKpisFrom(state.data).length === 0 ? (
            <DigitalEmpty
              icon="campaign"
              title={t('digital.emptyNoPeriod')}
              desc={t('digital.emptyNoPeriodDesc')}
              ctaLabel={t('digital.emptyNoSourceCta')}
              ctaTo="/sources"
            />
          ) : (
            <DigitalKpiGroup
              kpis={adsKpisFrom(state.data)}
              source="ads"
              groupLabel={t('digital.groupPaid')}
            />
          )}
        </>
      ) : null}

      {state.kind === 'ready' && adsKpisFrom(state.data).length > 0 ? (
        <Link
          to="/digital/campanhas"
          className="digital-campaigns-cta"
          aria-label={t('ads.viewCampaigns')}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            campaign
          </span>
          {t('ads.viewCampaigns')}
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            arrow_forward
          </span>
        </Link>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Definicao das abas
// ---------------------------------------------------------------------------

interface TabDef {
  readonly id: string;
  readonly labelKey:
    | 'digital.tabOverview'
    | 'digital.tabInstagram'
    | 'digital.tabMetaAds'
    | 'digital.tabCampaigns';
  readonly to: string;
  readonly icon: string;
  /** Prefixo de pathname que torna esta aba ativa (match parcial para sub-rotas) */
  readonly matchPrefix: string;
  /** Se true, e a rota index (ativa exatamente em /digital) */
  readonly isIndex?: boolean;
}

const DIGITAL_TABS: readonly TabDef[] = [
  {
    id: 'visao-geral',
    labelKey: 'digital.tabOverview',
    to: '/digital',
    icon: 'grid_view',
    matchPrefix: '/digital',
    isIndex: true,
  },
  {
    id: 'instagram',
    labelKey: 'digital.tabInstagram',
    to: '/digital/instagram',
    icon: 'photo_camera',
    matchPrefix: '/digital/instagram',
  },
  {
    id: 'meta-ads',
    labelKey: 'digital.tabMetaAds',
    to: '/digital/meta-ads',
    icon: 'campaign',
    matchPrefix: '/digital/meta-ads',
  },
  {
    id: 'campanhas',
    labelKey: 'digital.tabCampaigns',
    to: '/digital/campanhas',
    icon: 'bar_chart',
    matchPrefix: '/digital/campanhas',
  },
];

// ---------------------------------------------------------------------------
// DigitalPage — shell com tabbar
// ---------------------------------------------------------------------------

export function DigitalPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  // Determina qual aba esta ativa pelo pathname
  function getActiveTab(): TabDef {
    // Procura correspondencia mais especifica primeiro (nao-index)
    const nonIndex = DIGITAL_TABS.filter((tab) => tab.isIndex !== true);
    for (const tab of nonIndex) {
      if (location.pathname.startsWith(tab.matchPrefix)) return tab;
    }
    // Fallback: aba index (sempre existe)
    return DIGITAL_TABS[0] as TabDef;
  }

  const activeTab = getActiveTab();

  // Keyboard nav: setas esquerda/direita nas abas
  function handleTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number): void {
    const allTabs = DIGITAL_TABS;
    const lastIdx = allTabs.length - 1;
    if (e.key === 'ArrowRight') {
      const nextTab = allTabs[(idx + 1) % allTabs.length];
      if (nextTab !== undefined) void navigate(nextTab.to);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      const prevTab = allTabs[(idx - 1 + allTabs.length) % allTabs.length];
      if (prevTab !== undefined) void navigate(prevTab.to);
      e.preventDefault();
    } else if (e.key === 'Home') {
      const firstTab = allTabs[0];
      if (firstTab !== undefined) void navigate(firstTab.to);
      e.preventDefault();
    } else if (e.key === 'End') {
      const lastTab = allTabs[lastIdx];
      if (lastTab !== undefined) void navigate(lastTab.to);
      e.preventDefault();
    }
  }

  return (
    <section aria-labelledby="digital-page-title">
      {/* Page Header */}
      <div className="digital-page-header">
        <div className="digital-page-eyebrow">{t('digital.pageEyebrow')}</div>
        <h1 id="digital-page-title" className="digital-page-title">
          {t('digital.pageTitle')}
        </h1>
        <p className="digital-page-subtitle">{t('digital.pageSub')}</p>
      </div>

      {/* Tab Bar — role=tablist, aba ativa refletida na URL */}
      <nav role="tablist" aria-label={t('digital.pageTitle')} className="digital-tabbar">
        {DIGITAL_TABS.map((tab, idx) => {
          const isActive =
            tab.isIndex === true ? activeTab.id === 'visao-geral' : activeTab.id === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`digital-tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`digital-tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              className="digital-tab"
              onClick={() => void navigate(tab.to)}
              onKeyDown={(e) => handleTabKeyDown(e, idx)}
            >
              <span className="digital-tab-icon material-symbols-rounded" aria-hidden="true">
                {tab.icon}
              </span>
              {t(tab.labelKey)}
            </button>
          );
        })}
      </nav>

      {/* Conteudo da aba ativa via Outlet */}
      <Outlet />
    </section>
  );
}
